import { mutationOptions } from "@tanstack/react-query";
import { customerRootQueryKey } from "~/api/customer/queries";
import {
  cancelSubscription,
  confirmPaymentMethod,
  createPaymentMethodSession,
  getCancelSubscriptionMutationKey,
  getConfirmPaymentMethodMutationKey,
  getCreatePaymentMethodSessionMutationKey,
  getGetPaymentMethodQueryKey,
  getGetSubscriptionsQueryKey,
  getReactivateSubscriptionMutationKey,
  getSendCallMeRequestMutationKey,
  getSendContactRequestMutationKey,
  getSendRegisterInvoiceRequestMutationKey,
  getUpgradeSubscriptionMutationKey,
  reactivateSubscription,
  sendCallMeRequest,
  sendContactRequest,
  sendRegisterInvoiceRequest,
  upgradeSubscription,
} from "~/api/generated/customer/customer";
import {
  type CallMeRequest,
  type ContactMeRequest,
  type PriceLookupKeyEnum,
  type RegisterInvoiceRequest,
} from "~/api/generated/model";

export const customerMutations = {
  cancelSubscription: () =>
    mutationOptions({
      mutationKey: getCancelSubscriptionMutationKey(),
      mutationFn: async (subscriptionId: string) => {
        await cancelSubscription(subscriptionId);
      },
      onSuccess: async (_data, _vars, _onResult, { client }) => {
        await client.invalidateQueries({
          queryKey: getGetSubscriptionsQueryKey(),
        });
      },
    }),

  reactivateSubscription: () =>
    mutationOptions({
      mutationKey: getReactivateSubscriptionMutationKey(),
      mutationFn: async (subscriptionId: string) => {
        await reactivateSubscription(subscriptionId);
      },
      onSuccess: async (_data, _vars, _onResult, { client }) => {
        await client.invalidateQueries({
          queryKey: getGetSubscriptionsQueryKey(),
        });
      },
    }),

  createPaymentMethodSession: () =>
    mutationOptions({
      mutationKey: getCreatePaymentMethodSessionMutationKey(),
      mutationFn: () => createPaymentMethodSession(),
    }),

  confirmPaymentMethod: () =>
    mutationOptions({
      mutationKey: getConfirmPaymentMethodMutationKey(),
      mutationFn: async (paymentMethodId: string) => {
        await confirmPaymentMethod({ paymentMethodId });
      },
      onSuccess: async (_data, _vars, _onResult, { client }) => {
        await client.invalidateQueries({
          queryKey: getGetPaymentMethodQueryKey(),
        });
      },
    }),

  sendCallMeRequest: () =>
    mutationOptions({
      mutationKey: getSendCallMeRequestMutationKey(),
      retry: 0,
      mutationFn: async (request: CallMeRequest) => {
        await sendCallMeRequest(request);
      },
    }),

  sendRegisterInvoiceRequest: () =>
    mutationOptions({
      mutationKey: getSendRegisterInvoiceRequestMutationKey(),
      retry: 0,
      mutationFn: async (request: RegisterInvoiceRequest) => {
        await sendRegisterInvoiceRequest(request);
      },
    }),

  requestContact: () =>
    mutationOptions({
      mutationKey: getSendContactRequestMutationKey(),
      retry: 0,
      mutationFn: async (request: ContactMeRequest) => {
        await sendContactRequest(request);
      },
    }),

  upgradeSubscription: (postMutationHandler: () => Promise<unknown>) =>
    mutationOptions({
      mutationKey: getUpgradeSubscriptionMutationKey(),
      mutationFn: async (targetPriceLookupKey: PriceLookupKeyEnum) => {
        await upgradeSubscription({ targetPriceLookupKey });
        await postMutationHandler();
      },
      onSuccess: async (_data, _vars, _onResult, { client }) => {
        await client.invalidateQueries({ queryKey: customerRootQueryKey });
      },
    }),
};
